import React, { useEffect, useState } from "react";
import { Sparkles, ChevronRight, Edit3 } from "lucide-react";
import { GOLD } from "../constants/theme";
import { VideoThumb } from "./VideoThumb";
import { supabase } from "../../supabaseClient";
import { useAdmin } from "../shared/AdminContext";

// Monday of the week containing `d`. Sunday belongs to the week ending,
// not the new week, so getDay()===0 → step back 6 days.
function weekStartISO(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x.toISOString().slice(0, 10);
}

// Hand-curated weekly session pinned to every coach's dashboard. Authored
// from /admin/workout-of-week — no AI, no nightly cron. If admins forget
// to publish a week, the card falls back to a friendly placeholder with a
// shortcut to author it.
export function WorkoutOfWeek({ C, dark, onOpenDrill, onBuildPlan, onBuildCustom, narrow = false }) {
  const [workout, setWorkout] = useState(null);
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin, adminMode } = useAdmin();

  const weekStart = weekStartISO();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("workouts_of_week")
        .select("*")
        .eq("for_week_start", weekStart)
        .maybeSingle();
      if (cancelled) return;
      if (error) { setLoading(false); return; }
      setWorkout(data || null);
      if (data?.drill_ids?.length) {
        const { data: dd } = await supabase
          .from("drills")
          .select("id, title, category, duration, mux_playback_id, section")
          .in("id", data.drill_ids);
        if (cancelled) return;
        const map = new Map((dd || []).map(d => [d.id, d]));
        setDrills(data.drill_ids.map(id => map.get(id)).filter(Boolean));
      } else {
        setDrills([]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [weekStart]);

  const goToAuthor = () => { window.location.hash = "#/admin/content/workout"; };

  // One card surface for the whole of Today's selection: neutral, with gold
  // kept for the eyebrow and the one button that does something.
  const card = { boxSizing:"border-box", borderRadius:14, background:C.cardGradient, border:`1px solid ${C.cardBorder}` };
  const eyebrow = { fontSize:10.5, fontWeight:800, color:GOLD, letterSpacing:1.3, textTransform:"uppercase" };

  if (loading && !workout) {
    return (
      <div style={{...card,padding:"18px 22px",color:C.textMid,fontSize:13,display:"flex",alignItems:"center",gap:10,height:"100%"}}>
        <Sparkles size={14} color={GOLD}/> Loading this week's workout…
      </div>
    );
  }

  if (!workout) {
    return (
      <div style={{...card,padding:"20px 22px",display:"flex",alignItems:"center",gap:14,height:"100%"}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{...eyebrow,marginBottom:4}}>Workout of the week</div>
          <div style={{fontSize:13,color:C.textMid,lineHeight:1.5}}>This week's workout is on the way: check back shortly.</div>
        </div>
        {isAdmin && adminMode && (
          <button onClick={goToAuthor}
            style={{padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",
              border:"none",background:GOLD,color:"#111",display:"flex",alignItems:"center",gap:6}}>
            <Edit3 size={12}/> Author this week
          </button>
        )}
      </div>
    );
  }

  const pill = { display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6, borderRadius:10,
    fontFamily:"inherit", cursor:"pointer", minHeight:narrow ? 46 : 40 };

  return (
    <div style={{...card,height:"100%",padding:narrow ? "18px 0 18px 18px" : "20px 22px",
      display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:12,paddingRight:narrow ? 18 : 0}}>
        <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:4}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={eyebrow}>Workout of the week</span>
            {workout.focus && <span style={{fontSize:10.5,fontWeight:700,color:C.textMid,padding:"2px 8px",borderRadius:10,border:`1px solid ${C.border}`}}>{workout.focus}</span>}
          </div>
          <div style={{fontSize:19,fontWeight:800,color:C.text,lineHeight:1.25}}>{workout.title}</div>
          {workout.summary && (
            <div style={{fontSize:13,color:C.textMid,lineHeight:1.55}}>{workout.summary}</div>
          )}
        </div>
        {isAdmin && adminMode && (
          <button onClick={goToAuthor}
            title="Edit this week's workout"
            style={{padding:"7px 12px",borderRadius:8,fontSize:11.5,fontWeight:700,cursor:"pointer",
              border:`1px solid ${C.border}`,background:"transparent",color:C.textMid,display:"flex",alignItems:"center",gap:6}}>
            <Edit3 size={11}/> Edit
          </button>
        )}
      </div>

      {/* Four across on a desktop; on a phone a rail you swipe, so the
          drills cost one row of height instead of two. */}
      <div className={narrow ? "bam-rail" : undefined}
        style={narrow
          ? {display:"flex",gap:8,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollSnapType:"x mandatory",paddingRight:18,scrollbarWidth:"none"}
          : {display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))",gap:8}}>
        {drills.map((d, i) => (
          <div key={d.id} onClick={()=>onOpenDrill && onOpenDrill(d)}
            className="btn bam-thumb-card"
            style={{cursor:"pointer",borderRadius:10,overflow:"hidden",position:"relative",aspectRatio:"16/9",background:"#000",
              ...(narrow ? {flex:"0 0 150px",scrollSnapAlign:"start"} : {})}}>
            <VideoThumb playbackId={d.mux_playback_id} width={400} height={225} dim/>
            <div style={{position:"absolute",inset:0,background:"linear-gradient(transparent 35%, rgba(0,0,0,0.7))"}}/>
            <div style={{position:"absolute",top:6,left:7,fontSize:9.5,fontWeight:800,color:"#fff",background:"rgba(0,0,0,0.6)",padding:"2px 6px",borderRadius:4}}>
              {i+1}
            </div>
            <div style={{position:"absolute",bottom:6,left:7,right:7,fontSize:11.5,fontWeight:700,color:"#fff",lineHeight:1.25,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
              {d.title}
            </div>
          </div>
        ))}
      </div>

      <div style={{display:"flex",flexDirection:narrow ? "column" : "row",gap:8,alignItems:narrow ? "stretch" : "center",
        paddingRight:narrow ? 18 : 0,marginTop:"auto"}}>
        {onBuildPlan && (
          <button onClick={()=>onBuildPlan(workout, drills)}
            style={{...pill,padding:"0 16px",background:GOLD,color:"#111",fontSize:narrow ? 14 : 12.5,fontWeight:800,border:"none"}}>
            Build plan with these drills {!narrow && <ChevronRight size={14}/>}
          </button>
        )}
        {onBuildCustom && (
          <button onClick={onBuildCustom}
            title="Generate a quick custom workout from your own inputs"
            style={{...pill,padding:"0 14px",background:"transparent",color:C.textMid,fontSize:narrow ? 13.5 : 12.5,fontWeight:700,border:`1px solid ${C.border}`}}>
            Build your own
          </button>
        )}
      </div>
    </div>
  );
}

export default WorkoutOfWeek;
